import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { Button, StatusBadge } from '@/components/ui'
import { hasHtmlTags, sanitizeQuoteHtml } from '@/lib/safeHtml.ts'

/**
 * Zentrale, wiederverwendbare Dokument-Anzeige fuer SAP-B1-OData-Belege.
 * Wird im Kundenportal (CustomerDocumentDetailPage) UND in internen
 * SysMan-Seiten genutzt. Rendert einen Papier-Look mit Header, Positionen,
 * Summen etc. — so nah wie moeglich am Crystal-Reports-Drucklayout.
 */

export type SapDocumentType = 'quotation' | 'order' | 'delivery' | 'invoice' | 'credit'

export interface SapSenderInfo {
  name: string
  /** Mehrzeilige Adresse (wird zeilenweise gerendert). */
  address: string
  geschaeftsfuehrer?: string
  tel?: string
  fax?: string
  email?: string
  steuernummer?: string
  uid?: string
  bankName?: string
  swift?: string
  iban?: string
}

interface SapDocumentViewProps {
  doc: Record<string, unknown>
  type: SapDocumentType
  /** Builder fuer Ticket-Links. Default: `/tickets/{id}`. */
  ticketLinkBuilder?: (ticketId: string) => string
  /**
   * Absender-/Firmendaten fuer Kopf + Footer. Wenn nicht gesetzt, bleiben
   * die Absender-Inline-Zeile und der Footer-Block leer.
   */
  sender?: SapSenderInfo
  /**
   * Shows a toggle button that switches between customer view (BOM children
   * hidden) and full internal view. Useful when displaying BOM quotations.
   */
  showCustomerViewToggle?: boolean
}

interface DocLine {
  LineNum?: number
  ItemCode?: string
  ItemDescription?: string
  Dscription?: string
  LineType?: string       // 'I' = Item, 'T' = Text (Gruppen-Header)
  Quantity?: number
  UnitPrice?: number
  Price?: number
  PriceAfterVAT?: number
  DiscountPercent?: number
  LineTotal?: number
  Currency?: string
  MeasureUnit?: string
  UoMCode?: string
  TreeType?: string
  VisualOrder?: number
  VatPercent?: number
  TaxPercentagePerRow?: number
  /** SAP QUT1.Text — line extension / free text set per document line. May contain HTML. */
  FreeText?: string
}

/** SAP B1 DocumentSpecialLines entry (QUT10 / dslt_Text or dslt_Subtotal). */
interface SpecialLine {
  LineType: 'dslt_Text' | 'dslt_Subtotal'
  /** 0-based LineNum of the last article line before this special line; -1 = before first line. */
  AfterLineNumber: number
  LineText: string
}

// ─── Formatter ──────────────────────────────────────────────────────────────
function fmtDate(iso?: string) { return iso ? new Date(iso).toLocaleDateString('de-DE') : '—' }
function fmtMoney(n?: number, cur = 'EUR') {
  if (n == null) return '—'
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`
}
function fmtNumber(n?: number) {
  if (n == null) return '—'
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })
}
/** Normalisiert SAP-Adressen: `\r\n` und einzelne `\r` werden zu `\n`,
 *  damit `whitespace-pre-line` die Zeilenumbrueche rendert. */
function normalizeLineBreaks(s: string): string {
  return s.replace(/\r\n?/g, '\n')
}

// ─── Texte pro Belegtyp ─────────────────────────────────────────────────────
interface TypeCopy {
  docLabel: string
  numLabel: string
  dueLabel: string
  intro: string
  showSignatureBox: boolean
}
const TYPE_COPY: Record<SapDocumentType, TypeCopy> = {
  quotation: {
    docLabel: 'Angebot', numLabel: 'Angebotsnummer', dueLabel: 'Gültig bis',
    intro: 'es freut uns, dass Sie sich für unsere Produkte und Dienstleistungen interessieren. Gerne offerieren wir wie folgt.',
    showSignatureBox: true,
  },
  order: {
    docLabel: 'Auftrag', numLabel: 'Auftragsnummer', dueLabel: 'Liefertermin',
    intro: 'vielen Dank für Ihren Auftrag. Wir bestätigen wie folgt.',
    showSignatureBox: false,
  },
  delivery: {
    docLabel: 'Lieferschein', numLabel: 'Lieferscheinnummer', dueLabel: 'Liefertermin',
    intro: 'wir liefern wie folgt.',
    showSignatureBox: false,
  },
  invoice: {
    docLabel: 'Rechnung', numLabel: 'Belegnummer', dueLabel: 'Fälligkeitsdatum',
    intro: 'es freut uns, dass Sie sich für unsere Produkte und Dienstleistungen entschieden haben. Wir stellen wie folgt in Rechnung.',
    showSignatureBox: false,
  },
  credit: {
    docLabel: 'Gutschrift', numLabel: 'Gutschriftnummer', dueLabel: 'Datum',
    intro: 'wir erstellen folgende Gutschrift.',
    showSignatureBox: false,
  },
}

// ─── Ticket-Feld-Erkennung ──────────────────────────────────────────────────
function isTicketField(key: string): boolean { return /ticket/i.test(key) }

/** Erste Zeile der Beschreibung nach "Ticket #NNN" absuchen. */
function splitLineDescription(desc: string): { ticketMatch?: { id: string; suffix: string }; rest: string } {
  const normalized = normalizeLineBreaks(desc)
  const lines = normalized.split('\n')
  const first = lines[0] ?? ''
  const m = first.match(/Ticket\s*#?\s*(\d+)(.*)$/i)
  if (m) {
    return {
      ticketMatch: { id: m[1]!, suffix: (m[2] ?? '').replace(/^\s*\/\/\s*/, ' // ') },
      rest: lines.slice(1).join('\n').trim(),
    }
  }
  return { rest: lines.slice(1).join('\n').trim() }
}

// ─── BOM-Gruppierung (Sales-Stuecklisten) ────────────────────────────────────
// Only explicitly marked children are grouped to avoid incorrectly attaching
// independent positions (e.g. a separately billed warranty after a BOM) to
// the preceding parent.
//
// SAP B1 TreeType values for BOM ingredient lines: 'iIngredient', 'iSubProduct'.
// Legacy/compact values: 'C' (component), 'N' (non-tree grouped).
// The regex /child/i is kept for forward-compat with any variant names.
function isBomChild(l: DocLine): boolean {
  const tt = String(l.TreeType ?? '')
  return tt === 'iIngredient' || tt === 'iSubProduct' || /child/i.test(tt) || tt === 'C' || tt === 'N'
}
function isTextLine(l: DocLine): boolean {
  return String(l.LineType ?? '').toUpperCase() === 'T'
}

interface RenderGroup {
  /** Falls gesetzt: Rendert eine Sektions-Zeile (Ticket-Header o.ae.) ueber
   *  den darunterliegenden Positionen statt einer normalen Position. */
  sectionHeader?: { text: string; ticketId?: string }
  parent: DocLine
  children: DocLine[]
}

function buildRenderGroups(rawLines: DocLine[]): RenderGroup[] {
  const sorted = [...rawLines].sort((a, b) => {
    const aVo = a.VisualOrder ?? a.LineNum ?? 0
    const bVo = b.VisualOrder ?? b.LineNum ?? 0
    return aVo - bVo
  })
  // Text-Lines (`LineType='T'`) werden als Sektions-Header an die NAeCHSTE
  // Item-Line gehaengt. Alle uebrigen Lines: nur explizite BOM-Children
  // werden unter die zuletzt eroeffnete Parent-Gruppe gehaengt.
  const result: RenderGroup[] = []
  let pendingHeader: { text: string; ticketId?: string } | undefined
  for (const line of sorted) {
    if (isTextLine(line)) {
      const text = (line.ItemDescription ?? line.Dscription ?? '').trim()
      if (text.length > 0) {
        const m = text.match(/Ticket\s*#?\s*(\d+)/i)
        pendingHeader = { text, ticketId: m?.[1] }
      }
      continue
    }
    const last = result[result.length - 1]
    if (last && isBomChild(line)) {
      last.children.push(line)
    } else {
      result.push({ parent: line, children: [], sectionHeader: pendingHeader })
      pendingHeader = undefined
    }
  }
  return result
}

/** Name der Kontaktperson aus bekannten SAP-Feldern extrahieren. Direkter
 *  String (`AttentionUser`, `U_Ansprechpartner`, …) gewinnt gegenueber dem
 *  numerischen `ContactPersonCode` — der liefert ohne Lookup nur eine ID. */
function extractContactPersonName(doc: Record<string, unknown>): string | null {
  const candidates = [
    'AttentionUser',
    'U_Ansprechpartner',
    'U_Kontaktperson',
    'U_ContactPerson',
    'ContactPersonName',
  ]
  for (const k of candidates) {
    const v = doc[k]
    if (typeof v === 'string' && v.trim().length > 0) return v.trim()
  }
  return null
}

// ─── MwSt-Rate ableiten ─────────────────────────────────────────────────────
function deriveVatRate(doc: Record<string, unknown>, lines: DocLine[]): number | null {
  // Aus erster Line mit VatPercent / TaxPercentagePerRow.
  for (const l of lines) {
    const v = l.VatPercent ?? l.TaxPercentagePerRow
    if (typeof v === 'number' && v > 0) return v
  }
  // Ableitung aus Netto/MwSt (defensiv).
  const total = Number(doc.DocTotal ?? 0)
  const vat = Number(doc.VatSum ?? 0)
  if (total > vat && vat > 0) {
    const net = total - vat
    return Math.round((vat / net) * 10000) / 100
  }
  return null
}

// ─── Komponente ─────────────────────────────────────────────────────────────

const defaultTicketLink = (id: string) => `/tickets/${encodeURIComponent(id)}`

export function SapDocumentView({ doc, type, ticketLinkBuilder = defaultTicketLink, sender, showCustomerViewToggle = false }: SapDocumentViewProps) {
  const [customerView, setCustomerView] = useState(false)
  const copy = TYPE_COPY[type]
  const docNum = String(doc.DocNum ?? doc.DocEntry ?? '')
  const docDate = doc.DocDate as string | undefined
  const docDueDate = doc.DocDueDate as string | undefined
  const cardCode = (doc.CardCode as string | undefined) ?? '—'
  const cardName = (doc.CardName as string | undefined) ?? cardCode
  const currency = (doc.DocCurrency as string | undefined) ?? 'EUR'
  const status = (doc.DocumentStatus as string | undefined) ?? null
  const comments = normalizeLineBreaks((doc.Comments as string | undefined) ?? '')
  // Prefer backend-resolved name (_paymentMethodName) over the raw SAP code
  const paymentMethod = (doc._paymentMethodName as string | undefined) ?? (doc.PaymentMethod as string | undefined) ?? ''
  const billingAddress = normalizeLineBreaks((doc.Address as string | undefined) ?? '')
  const shippingAddress = normalizeLineBreaks((doc.Address2 as string | undefined) ?? '')
  const shippingSameAsBilling = shippingAddress === '' || shippingAddress === billingAddress
  // SAP liefert in `Address` meistens NUR Strasse + PLZ/Ort, nicht den Firmennamen.
  // Deshalb bauen wir den Empfaenger-Block aus `CardName` + `Address`.
  // Falls `Address` bereits mit dem CardName beginnt, wird das nicht dupliziert.
  const recipientFirstLine = billingAddress.split('\n')[0] ?? ''
  const recipientBlock = recipientFirstLine.trim() === cardName.trim()
    ? billingAddress
    : [cardName, billingAddress].filter((s) => s && s.trim().length > 0).join('\n')
  const contactPerson = extractContactPersonName(doc)

  const rawLines = (Array.isArray(doc.DocumentLines) ? doc.DocumentLines : []) as DocLine[]
  const groups = buildRenderGroups(rawLines)

  // Build map of DocumentSpecialLines keyed by AfterLineNumber for O(1) lookup.
  const rawSpecialLines = (Array.isArray(doc.DocumentSpecialLines) ? doc.DocumentSpecialLines : []) as SpecialLine[]
  const specialsByAfter = new Map<number, SpecialLine[]>()
  for (const sl of rawSpecialLines) {
    const key = sl.AfterLineNumber ?? -1
    const arr = specialsByAfter.get(key) ?? []
    arr.push(sl)
    specialsByAfter.set(key, arr)
  }

  /** Renders a single DocumentSpecialLines entry as a table row. */
  function renderSpecialLineRow(sl: SpecialLine, key: string) {
    if (sl.LineType === 'dslt_Text') {
      const text = normalizeLineBreaks(sl.LineText ?? '')
      return (
        <tr key={key} className="border-b border-gray-100 bg-blue-50/30">
          <td colSpan={6} className="px-3 py-2 text-small text-gray-700">
            {hasHtmlTags(text)
              ? <div dangerouslySetInnerHTML={{ __html: sanitizeQuoteHtml(text) }} />
              : <span className="whitespace-pre-line">{text}</span>}
          </td>
        </tr>
      )
    }
    if (sl.LineType === 'dslt_Subtotal') {
      return (
        <tr key={key} className="border-b border-gray-200 bg-gray-50">
          <td colSpan={5} className="px-3 py-2 text-small font-semibold text-gray-700 text-right">
            {sl.LineText?.trim() || 'Zwischensumme'}
          </td>
          <td className="px-3 py-2 text-right text-small text-gray-400 italic">SAP berechnet</td>
        </tr>
      )
    }
    return null
  }
  const netTotal = (doc.DocTotal as number | undefined ?? 0) - (doc.VatSum as number | undefined ?? 0)
  const vatSum = (doc.VatSum as number | undefined) ?? 0
  const docTotal = (doc.DocTotal as number | undefined) ?? 0
  const vatRate = deriveVatRate(doc, rawLines)

  // Ticket-Referenz auf Header-Ebene (z.B. U_OST_TS_TicketID).
  const headerTicketEntry = Object.entries(doc).find(([k, v]) =>
    k.startsWith('U_') && isTicketField(k) && v != null && String(v) !== ''
  )
  const headerTicketId = headerTicketEntry ? String(headerTicketEntry[1]) : null

  // Absender-Inline-Zeile wie im Original: "Name • Adresse (einzeilig)".
  const senderInline = sender
    ? [sender.name, normalizeLineBreaks(sender.address).split('\n').join(' ')]
        .filter(Boolean).join(' • ')
    : null

  return (
    <div className="bg-white shadow-card rounded-lg p-8 sm:p-12 mx-auto w-full max-w-4xl text-body text-gray-900">
      {/* Absender-Inline-Zeile */}
      {senderInline && (
        <div className="text-caption text-gray-600 mb-6">{senderInline}</div>
      )}

      {/* Header: Logo + Empfaenger links | Titel + Metadaten rechts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div>
          {/* implec-Schriftzug als Text-Logo (gross, brand-Rot, fett).
              Es gibt aktuell kein Logo-SVG im Repo — die Wortmarke wird
              textbasiert gerendert. */}
          <div className="text-[2.25rem] font-extrabold text-brand-700 leading-none tracking-tight lowercase">
            {sender?.name?.toLowerCase().startsWith('implec') ? 'implec' : (sender?.name ?? 'implec')}
          </div>
          <div className="mt-8 text-small whitespace-pre-line">
            {recipientBlock || `${cardName}\nKundennr. ${cardCode}`}
          </div>
        </div>
        <div>
          <h1 className="text-h1 font-bold">{copy.docLabel}</h1>
          <table className="mt-4 w-full text-small border-t border-gray-200">
            <thead>
              <tr className="text-caption text-gray-500 text-left">
                <th className="py-1 font-medium">{copy.numLabel}</th>
                <th className="py-1 font-medium">Kundennr.</th>
                <th className="py-1 font-medium">Datum</th>
              </tr>
            </thead>
            <tbody>
              <tr className="tabular-nums">
                <td className="py-1">{docNum}</td>
                <td className="py-1">{cardCode}</td>
                <td className="py-1">{fmtDate(docDate)}</td>
              </tr>
            </tbody>
          </table>
          {(docDueDate || status) && (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-small items-center">
              {docDueDate && (
                <>
                  <dt className="text-gray-500">{copy.dueLabel}</dt>
                  <dd className="text-right">{fmtDate(docDueDate)}</dd>
                </>
              )}
              {status && (
                <>
                  <dt className="text-gray-500">Status</dt>
                  <dd className="text-right"><StatusBadge status={status} /></dd>
                </>
              )}
            </dl>
          )}
        </div>
      </div>

      {/* Info-Block: Ticketnummer, Kontaktperson, Lieferadresse */}
      <div className="mt-6 border-t border-gray-200 pt-4 space-y-2 text-small">
        {headerTicketId && (
          <div className="flex justify-between">
            <span className="text-gray-500">Ticketnummer</span>
            <Link
              to={ticketLinkBuilder(headerTicketId)}
              className="text-brand-700 hover:text-brand-800 hover:underline font-medium"
            >
              #{headerTicketId}
            </Link>
          </div>
        )}
        {contactPerson ? (
          <div className="flex justify-between">
            <span className="text-gray-500">Kontaktperson</span>
            <span>{contactPerson}</span>
          </div>
        ) : null}
        {!shippingSameAsBilling && shippingAddress ? (
          <div>
            <div className="text-gray-500 font-medium">Lieferadresse</div>
            <div className="mt-1 whitespace-pre-line">{shippingAddress}</div>
          </div>
        ) : shippingSameAsBilling && billingAddress ? (
          <div className="text-caption text-gray-500">Lieferadresse ist gleich Rechnungsadresse.</div>
        ) : null}
      </div>

      {/* Anrede + Einleitungstext */}
      <p className="mt-6">Sehr geehrte Damen und Herren,</p>
      <p className="mt-4">{copy.intro}</p>

      {/* Positionen */}
      {groups.length > 0 && (() => {
        const hasBomGroups = groups.some((g) => g.children.length > 0)
        return (
          <>
            {showCustomerViewToggle && hasBomGroups && (
              <div className="mt-6 flex items-center justify-end gap-2">
                <span className="text-caption text-gray-500">
                  {customerView ? 'Kundenansicht (Stückliste zusammengefasst)' : 'Interne Ansicht (alle Positionen)'}
                </span>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setCustomerView((v) => !v)}
                  className="gap-1.5"
                >
                  {customerView
                    ? <><EyeOff className="w-3.5 h-3.5" /> Vollansicht</>
                    : <><Eye className="w-3.5 h-3.5" /> Kundenansicht</>}
                </Button>
              </div>
            )}
            <table className={`${showCustomerViewToggle && hasBomGroups ? 'mt-2' : 'mt-6'} w-full text-small`}>
          <thead>
            <tr className="bg-brand-700 text-white">
              <th className="text-left px-3 py-2 font-semibold w-12">Pos</th>
              <th className="text-right px-3 py-2 font-semibold w-20">Menge</th>
              <th className="text-left px-3 py-2 font-semibold w-16">Einh.</th>
              <th className="text-left px-3 py-2 font-semibold">Artikelnr. / Beschreibung</th>
              <th className="text-right px-3 py-2 font-semibold w-28">Preis {currency}</th>
              <th className="text-right px-3 py-2 font-semibold w-28">Gesamt {currency}</th>
            </tr>
          </thead>
          <tbody>
            {/* Special lines that appear before the first document line (AfterLineNumber = -1) */}
            {(specialsByAfter.get(-1) ?? []).map((sl, si) => renderSpecialLineRow(sl, `spec-pre-${si}`))}
            {groups.map(({ parent: l, children, sectionHeader }, idx) => {
              const unitPrice = l.UnitPrice ?? l.Price
              const uom = l.MeasureUnit ?? l.UoMCode ?? ''
              const description = normalizeLineBreaks(l.ItemDescription ?? l.Dscription ?? '')
              const firstLine = description.split('\n')[0] ?? ''
              const { ticketMatch, rest } = splitLineDescription(description)
              // Child-Beschreibungen sammeln — erste Child entfernen, wenn sie
              // mit dem Parent-Titel uebereinstimmt (BOM-Layout doppelt sonst).
              let childDescriptions = children
                .map((c) => normalizeLineBreaks(c.ItemDescription ?? c.Dscription ?? c.ItemCode ?? ''))
                .filter((s) => s.trim().length > 0)
              if (childDescriptions.length > 0 && childDescriptions[0]!.trim() === firstLine.trim()) {
                childDescriptions = childDescriptions.slice(1)
              }
              // Collect all special lines for this group (parent + children LineNums).
              // Since children are rendered inline, all their special lines appear after the group row.
              const groupLineNums: number[] = []
              if (l.LineNum != null) groupLineNums.push(l.LineNum)
              for (const child of children) {
                if (child.LineNum != null) groupLineNums.push(child.LineNum)
              }
              const groupSpecials = groupLineNums.flatMap((n) => specialsByAfter.get(n) ?? [])

              // FreeText (QUT1.Text) — may contain HTML from the AI.
              const freeText = l.FreeText?.trim() ?? ''

              return (
                <>
                  {sectionHeader && (
                    <tr key={`hdr-${idx}`} className="bg-brand-50">
                      <td colSpan={6} className="px-3 py-2 font-medium text-gray-900 whitespace-pre-line">
                        {sectionHeader.ticketId ? (
                          <>
                            <Link
                              to={ticketLinkBuilder(sectionHeader.ticketId)}
                              className="text-brand-700 hover:text-brand-800 hover:underline"
                            >
                              Ticket #{sectionHeader.ticketId}
                            </Link>
                            {sectionHeader.text.replace(/^Ticket\s*#?\s*\d+/i, '')}
                          </>
                        ) : sectionHeader.text}
                      </td>
                    </tr>
                  )}
                  <tr key={`row-${l.LineNum ?? idx}-${l.ItemCode ?? idx}`} className="border-b border-gray-200 align-top">
                    <td className="px-3 py-3 tabular-nums">{idx + 1}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNumber(l.Quantity)}</td>
                    <td className="px-3 py-3">{uom || '—'}</td>
                    <td className="px-3 py-3">
                      {l.ItemCode && (
                        <div className="text-caption text-gray-500">{l.ItemCode}</div>
                      )}
                      <div className="font-medium">
                        {ticketMatch ? (
                          <>
                            <Link
                              to={ticketLinkBuilder(ticketMatch.id)}
                              className="text-brand-700 hover:text-brand-800 hover:underline"
                            >
                              Ticket #{ticketMatch.id}
                            </Link>
                            {ticketMatch.suffix}
                          </>
                        ) : description ? (
                          <span>{firstLine}</span>
                        ) : '—'}
                      </div>
                      {rest && (
                        <div className="mt-2 text-caption text-gray-600 whitespace-pre-line">{rest}</div>
                      )}
                      {/* QUT1.Text line extension — rendered as sanitized HTML when HTML is detected */}
                      {freeText && (
                        <div className="mt-1 text-caption text-gray-600">
                          {hasHtmlTags(freeText)
                            ? <div dangerouslySetInnerHTML={{ __html: sanitizeQuoteHtml(freeText) }} />
                            : <span className="whitespace-pre-line">{freeText}</span>}
                        </div>
                      )}
                      {!customerView && childDescriptions.length > 0 && (
                        <ul className="mt-2 text-caption text-gray-700 list-none space-y-0.5">
                          {childDescriptions.map((d, i) => (
                            <li key={i} className="whitespace-pre-line">{d}</li>
                          ))}
                        </ul>
                      )}
                      {customerView && childDescriptions.length > 0 && (
                        <div className="mt-1 text-caption text-gray-400 italic">
                          {childDescriptions.length} Pos. intern
                        </div>
                      )}
                      {l.DiscountPercent && l.DiscountPercent > 0 ? (
                        <div className="mt-1 text-caption text-gray-500">Rabatt {l.DiscountPercent}%</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtMoney(unitPrice, currency)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">{fmtMoney(l.LineTotal, currency)}</td>
                  </tr>
                  {/* DocumentSpecialLines for this group (dslt_Text / dslt_Subtotal) */}
                  {groupSpecials.map((sl, si) => renderSpecialLineRow(sl, `spec-${idx}-${si}`))}
                </>
              )
            })}
          </tbody>
        </table>
          </>
        )
      })()}

      {/* Summen */}
      <div className="mt-6 flex justify-end">
        <dl className="w-full max-w-sm text-small">
          {vatSum > 0 && (
            <>
              <div className="flex justify-between py-1 border-b border-gray-200">
                <dt>Nettobetrag</dt>
                <dd className="tabular-nums">{fmtMoney(netTotal, currency)}</dd>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-200">
                <dt>
                  {vatRate != null
                    ? `MwSt. ${vatRate.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} % auf ${netTotal.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : 'MwSt.'}
                </dt>
                <dd className="tabular-nums">{fmtMoney(vatSum, currency)}</dd>
              </div>
            </>
          )}
          <div className="flex justify-between py-2 font-bold text-body border-t-2 border-gray-900">
            <dt>Gesamtsumme</dt>
            <dd className="tabular-nums">{fmtMoney(docTotal, currency)}</dd>
          </div>
        </dl>
      </div>

      {/* Zahlungsbedingung + Gueltigkeit */}
      {(paymentMethod || comments || (type === 'quotation' && docDueDate)) && (
        <dl className="mt-8 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-small">
          {paymentMethod && (
            <>
              <dt className="text-gray-500">Zahlungsbedingung:</dt>
              <dd>{paymentMethod}</dd>
            </>
          )}
          {type === 'quotation' && docDueDate && (
            <>
              <dt className="text-gray-500">Angebot gültig bis:</dt>
              <dd>{fmtDate(docDueDate)}</dd>
            </>
          )}
          {comments && (
            <>
              <dt className="text-gray-500">Bemerkungen:</dt>
              <dd className="whitespace-pre-line">{comments}</dd>
            </>
          )}
        </dl>
      )}

      {/* Unterschriften-Box (nur bei Angeboten) */}
      {copy.showSignatureBox && (
        <div className="mt-10 flex justify-end">
          <div className="w-full max-w-md border border-gray-300 rounded p-4 text-small">
            <div className="text-gray-700">Angebot hiermit angenommen:</div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="border-t border-gray-400 pt-1 text-caption text-gray-500">Datum</div>
              <div className="border-t border-gray-400 pt-1 text-caption text-gray-500">Unterschrift, Stempel</div>
            </div>
          </div>
        </div>
      )}

      {/* Footer mit Firmen- und Bankdaten */}
      {sender && (
        <div className="mt-12 pt-4 border-t border-gray-200 grid grid-cols-2 sm:grid-cols-4 gap-4 text-caption text-gray-700">
          <div className="whitespace-pre-line">
            <div className="font-medium">{sender.name}</div>
            <div>{normalizeLineBreaks(sender.address)}</div>
          </div>
          <div>
            {sender.geschaeftsfuehrer && (<><div className="font-medium">Geschäftsführer</div><div>{sender.geschaeftsfuehrer}</div></>)}
            {sender.tel && (<><div className="mt-1 font-medium">Tel.</div><div>{sender.tel}</div></>)}
            {sender.fax && (<><div className="mt-1 font-medium">Fax</div><div>{sender.fax}</div></>)}
          </div>
          <div>
            {sender.email && (<><div className="font-medium">E-Mail</div><div>{sender.email}</div></>)}
            {sender.steuernummer && (<><div className="mt-1 font-medium">Steuernummer</div><div>{sender.steuernummer}</div></>)}
            {sender.uid && (<><div className="mt-1 font-medium">UID</div><div>{sender.uid}</div></>)}
          </div>
          <div>
            {sender.bankName && (<><div className="font-medium">Bankverbindung</div><div>{sender.bankName}</div></>)}
            {sender.swift && (<><div className="mt-1 font-medium">SWIFT/BIC</div><div className="tabular-nums">{sender.swift}</div></>)}
            {sender.iban && (<><div className="mt-1 font-medium">IBAN</div><div className="tabular-nums">{sender.iban}</div></>)}
          </div>
        </div>
      )}
    </div>
  )
}
